import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, Music2, X, SkipForward, SkipBack } from "lucide-react";

/**
 * Parse a YouTube URL into either a single video or a playlist track.
 * Returns null if not a valid YouTube URL.
 *   - { kind: 'playlist', listId, url } for ?list=PLxxxx URLs (incl. playlist-only links)
 *   - { kind: 'video',    videoId, url } for single video URLs
 *
 * Treat playlist URLs first so that `?v=...&list=...` is played as a playlist
 * (loops through every track), not as just the single video.
 */
function parseYouTubeTrack(url) {
  if (!url) return null;
  try {
    const u = new URL(String(url).trim());
    const host = u.hostname.toLowerCase();
    const isYouTube =
      host.includes("youtu.be") ||
      host.includes("youtube.com") ||
      host.includes("youtube-nocookie.com");
    if (!isYouTube) return null;

    // 1) Playlist URL — looks like ?list=PL... / RD... / OL... / UU... etc.
    const listId = u.searchParams.get("list");
    if (listId && /^[A-Za-z0-9_-]{10,}$/.test(listId)) {
      return { kind: "playlist", listId, url: String(url) };
    }

    // 2) Single video URL
    if (host.includes("youtu.be")) {
      const id = u.pathname.slice(1).split(/[/?#]/)[0];
      if (id) return { kind: "video", videoId: id, url: String(url) };
    } else {
      const v = u.searchParams.get("v");
      if (v) return { kind: "video", videoId: v, url: String(url) };
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => ["embed", "shorts", "v", "live"].includes(p));
      if (idx >= 0 && parts[idx + 1]) {
        return { kind: "video", videoId: parts[idx + 1], url: String(url) };
      }
    }
  } catch (_) { /* ignore */ }
  return null;
}

// Backwards-compat shim — some callers (tests, etc.) still use the old name.
function parseYouTubeId(url) {
  const t = parseYouTubeTrack(url);
  return t && t.kind === "video" ? t.videoId : null;
}

const PLAYER_DIV_ID = "nm-yt-audio-player";
const VOLUME_KEY = "nm_audio_volume";
const ENABLED_KEY = "nm_audio_enabled";

function loadYouTubeApi() {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve();
    if (!document.getElementById("yt-iframe-api-script")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api-script";
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve();
    };
  });
}

const fmtTime = (s) => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  // Defensive cap — YouTube returns absurd values for livestreams (e.g. 221466:39).
  // Anything beyond 24h almost certainly means it's a live/24-7 stream — show "LIVE".
  if (s >= 86400) return "LIVE";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export default function AudioPlayer({ urls = [] }) {
  // Normalize: filter out empty + parse to {kind, videoId|listId, url} tracks.
  const tracks = (urls || [])
    .map((u) => parseYouTubeTrack(u))
    .filter(Boolean);

  const playerRef = useRef(null);
  // Cache the first videoId of each playlist so we can render a thumbnail
  // before the player has loaded. Keyed by listId.
  const playlistThumbRef = useRef({});
  const [index, setIndex] = useState(0);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [meta, setMeta] = useState({ title: "", author: "" });
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);
  const [volume, setVolume] = useState(() => {
    const v = parseInt(localStorage.getItem(VOLUME_KEY) || "50", 10);
    return isNaN(v) ? 50 : Math.max(0, Math.min(100, v));
  });
  const [enabled, setEnabled] = useState(() => {
    const stored = localStorage.getItem(ENABLED_KEY);
    return stored !== "0";
  });
  const [collapsed, setCollapsed] = useState(false);
  const [hidden, setHidden] = useState(false);
  // The videoId of the currently-playing item within a playlist (so we can
  // render the right thumbnail while YT advances internally).
  const [activeVideoId, setActiveVideoId] = useState(null);

  const track = tracks[index];
  const thumbVideoId =
    activeVideoId ||
    (track?.kind === "video"
      ? track.videoId
      : track?.kind === "playlist"
      ? playlistThumbRef.current[track.listId] || null
      : null);
  const thumbUrl = thumbVideoId
    ? `https://img.youtube.com/vi/${thumbVideoId}/hqdefault.jpg`
    : "";

  // Reset index if tracks change
  useEffect(() => {
    if (tracks.length === 0) {
      setIndex(0);
    } else if (index >= tracks.length) {
      setIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks.length]);

  // Build / rebuild player when the active track key changes.
  // Track key = "v:<videoId>" or "p:<listId>" so videoId switches within a
  // playlist (advanced internally by YT) don't trigger a full rebuild.
  const trackKey = track
    ? track.kind === "playlist"
      ? `p:${track.listId}`
      : `v:${track.videoId}`
    : null;

  useEffect(() => {
    if (!track) return;
    let cancelled = false;

    // Helper: tell the existing player to load the new track
    const loadTrack = () => {
      if (!playerRef.current) return;
      try {
        if (track.kind === "playlist") {
          playerRef.current.loadPlaylist({
            list: track.listId,
            listType: "playlist",
            index: 0,
            suggestedQuality: "small",
          });
          // Native loop — YT will replay the playlist when it reaches the end
          setTimeout(() => {
            try { playerRef.current?.setLoop?.(true); } catch (_) { /* ignore */ }
          }, 1500);
        } else {
          // Single video — turn loop off (we manage advancement via state)
          try { playerRef.current?.setLoop?.(false); } catch (_) { /* ignore */ }
          playerRef.current.loadVideoById(track.videoId);
        }
      } catch (_) { /* ignore */ }
    };

    loadYouTubeApi().then(() => {
      if (cancelled) return;
      const el = document.getElementById(PLAYER_DIV_ID);
      if (!el) return;

      if (playerRef.current && typeof playerRef.current.loadVideoById === "function") {
        loadTrack();
        return;
      }

      // First-time construction
      const initialVars = {
        autoplay: 1,
        controls: 0,
        modestbranding: 1,
        playsinline: 1,
        disablekb: 1,
        fs: 0,
        iv_load_policy: 3,
        rel: 0,
      };
      const baseConfig = {
        height: "0",
        width: "0",
        playerVars: initialVars,
        events: {
          onReady: (e) => {
            if (cancelled) return;
            setReady(true);
            try {
              e.target.setVolume(volume);
              e.target.mute();
              if (track.kind === "playlist") {
                // setLoop must be called *after* the playlist has started cuing
                setTimeout(() => {
                  try { e.target.setLoop?.(true); } catch (_) { /* ignore */ }
                }, 1500);
              }
              e.target.playVideo();
            } catch (_) { /* ignore */ }
            // unmute on first user interaction
            const tryUnmute = () => {
              if (cancelled || !playerRef.current) return;
              try {
                // Read fresh from localStorage so we honour the user's most
                // recently saved volume even after reloads / closure capture.
                const savedRaw = localStorage.getItem(VOLUME_KEY);
                const savedVol = Math.max(0, Math.min(100, parseInt(savedRaw ?? "50", 10) || 0));
                const enabledNow = localStorage.getItem(ENABLED_KEY) !== "0";

                // If the user had previously turned volume to 0 or disabled
                // the player, leave it muted on first interaction — don't
                // surprise them with sudden audio.
                if (!enabledNow || savedVol === 0) {
                  playerRef.current.mute();
                  playerRef.current.setVolume(savedVol);
                } else {
                  playerRef.current.unMute();
                  playerRef.current.setVolume(savedVol);
                  playerRef.current.playVideo();
                }
              } catch (_) { /* ignore */ }
              window.removeEventListener("pointerdown", tryUnmute);
              window.removeEventListener("keydown", tryUnmute);
              window.removeEventListener("scroll", tryUnmute);
            };
            window.addEventListener("pointerdown", tryUnmute, { once: true });
            window.addEventListener("keydown", tryUnmute, { once: true });
            window.addEventListener("scroll", tryUnmute, { once: true, passive: true });
          },
          onStateChange: (e) => {
            const PS = window.YT.PlayerState;
            if (e.data === PS.PLAYING) {
              setPlaying(true);
              try {
                const data = e.target.getVideoData?.();
                if (data) {
                  setMeta({ title: data.title || "", author: data.author || "" });
                  // Cache first video of playlist for thumbnail rendering
                  if (data.video_id) {
                    setActiveVideoId(data.video_id);
                    if (track?.kind === "playlist") {
                      playlistThumbRef.current[track.listId] =
                        playlistThumbRef.current[track.listId] || data.video_id;
                    }
                  }
                }
                setDuration(e.target.getDuration?.() || 0);
              } catch (_) { /* ignore */ }
            } else if (e.data === PS.PAUSED) {
              setPlaying(false);
            } else if (e.data === PS.ENDED) {
              // For single video: advance to next queue entry (wraps to start).
              // For a looping playlist YT replays internally and this branch
              // shouldn't fire — but if loop didn't engage, advance anyway.
              setIndex((i) => (tracks.length ? (i + 1) % tracks.length : 0));
            }
          },
        },
      };

      if (track.kind === "playlist") {
        // Construct with list params instead of videoId
        playerRef.current = new window.YT.Player(PLAYER_DIV_ID, {
          ...baseConfig,
          playerVars: {
            ...initialVars,
            list: track.listId,
            listType: "playlist",
            loop: 1,
            // Required for loop=1 to work on single-video, harmless for playlists
            playlist: track.listId,
          },
        });
      } else {
        playerRef.current = new window.YT.Player(PLAYER_DIV_ID, {
          ...baseConfig,
          videoId: track.videoId,
        });
      }
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackKey]);

  // Apply volume + mute state (and persist both so refresh restores them)
  useEffect(() => {
    if (!ready || !playerRef.current) return;
    try {
      playerRef.current.setVolume(volume);
      if (!enabled || volume === 0) playerRef.current.mute();
      else playerRef.current.unMute();
    } catch (_) { /* ignore */ }
    localStorage.setItem(VOLUME_KEY, String(volume));
    localStorage.setItem(ENABLED_KEY, enabled ? "1" : "0");
  }, [volume, enabled, ready]);

  // Poll current time + duration for progress bar
  useEffect(() => {
    if (!ready) return;
    const iv = setInterval(() => {
      if (!playerRef.current || scrubbing) return;
      try {
        const t = playerRef.current.getCurrentTime?.() || 0;
        const d = playerRef.current.getDuration?.() || 0;
        setCurrent(t);
        if (d && d !== duration) setDuration(d);
      } catch (_) { /* ignore */ }
    }, 500);
    return () => clearInterval(iv);
  }, [ready, scrubbing, duration]);

  // Pull metadata once a moment after track switch (in case state event missed)
  useEffect(() => {
    if (!ready || !playerRef.current) return;
    const t = setTimeout(() => {
      try {
        const data = playerRef.current.getVideoData?.();
        if (data) setMeta({ title: data.title || "", author: data.author || "" });
        setDuration(playerRef.current.getDuration?.() || 0);
      } catch (_) { /* ignore */ }
    }, 1200);
    return () => clearTimeout(t);
  }, [track?.videoId, ready]);

  const togglePlay = useCallback(() => {
    if (!ready || !playerRef.current) return;
    try {
      if (playing) playerRef.current.pauseVideo();
      else {
        if (!enabled) {
          setEnabled(true);
          localStorage.setItem(ENABLED_KEY, "1");
          playerRef.current.unMute();
        }
        playerRef.current.playVideo();
      }
    } catch (_) { /* ignore */ }
  }, [ready, playing, enabled]);

  const skipForward = () => {
    // Within a playlist, advance the playlist; otherwise advance the queue.
    if (track?.kind === "playlist" && playerRef.current?.nextVideo) {
      try { playerRef.current.nextVideo(); return; } catch (_) { /* fallthrough */ }
    }
    setIndex((i) => (tracks.length ? (i + 1) % tracks.length : 0));
  };
  const skipBack = () => {
    if (track?.kind === "playlist" && playerRef.current?.previousVideo) {
      try { playerRef.current.previousVideo(); return; } catch (_) { /* fallthrough */ }
    }
    setIndex((i) => (tracks.length ? (i - 1 + tracks.length) % tracks.length : 0));
  };

  const seek = (pct) => {
    if (!ready || !playerRef.current || !duration) return;
    const t = (pct / 100) * duration;
    try {
      playerRef.current.seekTo(t, true);
      setCurrent(t);
    } catch (_) { /* ignore */ }
  };

  const enableAudio = () => {
    if (!ready || !playerRef.current) return;
    setEnabled(true);
    localStorage.setItem(ENABLED_KEY, "1");
    try {
      playerRef.current.unMute();
      playerRef.current.setVolume(volume || 50);
      playerRef.current.playVideo();
    } catch (_) { /* ignore */ }
  };

  if (tracks.length === 0) return null;
  if (hidden) {
    return (
      <div className="fixed left-[-9999px] top-[-9999px] pointer-events-none" aria-hidden="true">
        <div id={PLAYER_DIV_ID} />
      </div>
    );
  }

  const muted = !enabled || volume === 0;
  const pct = duration ? (current / duration) * 100 : 0;
  const displayTitle = meta.title || "loading...";

  return (
    <>
      <div className="fixed left-[-9999px] top-[-9999px] pointer-events-none" aria-hidden="true">
        <div id={PLAYER_DIV_ID} />
      </div>

      <div
        data-testid="audio-player"
        className="fixed z-[60] bottom-5 right-5 select-none font-body"
      >
        {collapsed ? (
          /* Collapsed — compact pill, just artwork + play */
          <div
            className="flex items-center gap-2 bg-[var(--theme-surface)]/90 backdrop-blur-xl border border-[var(--theme-border)] rounded-full pl-1 pr-3 py-1 shadow-[0_12px_36px_-10px_rgba(0,0,0,0.7)]"
          >
            <button
              data-testid="audio-play-toggle"
              onClick={togglePlay}
              disabled={!ready}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-[var(--theme-accent-primary)] text-black hover:scale-105 active:scale-95 transition disabled:opacity-40"
              aria-label={playing ? "pause" : "play"}
            >
              {playing ? (
                <Pause size={12} strokeWidth={2.5} />
              ) : (
                <Play size={12} strokeWidth={2.5} className="ml-0.5" />
              )}
            </button>
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                playing ? "bg-[var(--theme-accent-primary)] animate-pulse" : "bg-[var(--theme-text-muted)]"
              }`}
            />
            <button
              data-testid="audio-collapse"
              onClick={() => setCollapsed(false)}
              className="text-[10px] tracking-[0.2em] text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] uppercase font-semibold transition"
              aria-label="expand"
            >
              Null FM
            </button>
          </div>
        ) : (
          /* Expanded — sleek horizontal mini-player */
          <div className="bg-[var(--theme-surface)]/95 backdrop-blur-xl border border-[var(--theme-border)] shadow-[0_18px_50px_-12px_rgba(0,0,0,0.7)] rounded-2xl w-[300px] overflow-hidden">
            <div className="flex items-center gap-3 p-2.5">
              {/* Thumbnail */}
              <div className="relative w-10 h-10 flex-shrink-0 bg-[var(--theme-bg)] overflow-hidden rounded-lg">
                {thumbUrl ? (
                  <img
                    key={thumbUrl}
                    src={thumbUrl}
                    alt=""
                    className={`w-full h-full object-cover ${playing ? "" : "opacity-70"}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Music2 size={14} className="text-[var(--theme-text-muted)]" />
                  </div>
                )}
              </div>

              {/* Title + author */}
              <div className="min-w-0 flex-1">
                <div className="text-[var(--theme-text)] text-[11px] leading-tight font-semibold tracking-tight truncate">
                  {displayTitle}
                </div>
                <div className="text-[9px] text-[var(--theme-text-muted)] uppercase tracking-[0.18em] mt-0.5 truncate">
                  {meta.author || "Null FM"}
                </div>
              </div>

              {/* Compact controls */}
              <button
                data-testid="audio-prev"
                onClick={skipBack}
                disabled={tracks.length < 2 && track?.kind !== "playlist"}
                className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] transition disabled:opacity-30"
                aria-label="previous track"
              >
                <SkipBack size={12} />
              </button>
              <button
                data-testid="audio-play-toggle"
                onClick={togglePlay}
                disabled={!ready}
                className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--theme-accent-primary)] text-black hover:scale-105 active:scale-95 transition disabled:opacity-40"
                aria-label={playing ? "pause" : "play"}
              >
                {playing ? <Pause size={11} strokeWidth={2.5} /> : <Play size={11} strokeWidth={2.5} className="ml-0.5" />}
              </button>
              <button
                data-testid="audio-next"
                onClick={skipForward}
                disabled={tracks.length < 2 && track?.kind !== "playlist"}
                className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] transition disabled:opacity-30"
                aria-label="next track"
              >
                <SkipForward size={12} />
              </button>
              <button
                data-testid="audio-collapse"
                onClick={() => setCollapsed(true)}
                className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] transition ml-1"
                aria-label="minimize"
              >
                <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="5.5" width="8" height="1.5" rx="0.75"/></svg>
              </button>
              <button
                data-testid="audio-hide"
                onClick={() => setHidden(true)}
                className="text-[var(--theme-text-muted)] hover:text-red-400 transition"
                aria-label="hide"
              >
                <X size={11} />
              </button>
            </div>

            {/* Slim progress bar (no time labels when LIVE) */}
            <div className="px-2.5 pb-2">
              <div className="relative h-[2px] bg-[var(--theme-border)] rounded-full overflow-hidden group">
                <div
                  className="absolute inset-y-0 left-0 bg-[var(--theme-accent-primary)] transition-[width] duration-200"
                  style={{ width: `${pct}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={pct || 0}
                  onMouseDown={() => setScrubbing(true)}
                  onMouseUp={(e) => { setScrubbing(false); seek(parseFloat(e.target.value)); }}
                  onTouchStart={() => setScrubbing(true)}
                  onTouchEnd={(e) => { setScrubbing(false); seek(parseFloat(e.target.value)); }}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (scrubbing) setCurrent((v / 100) * (duration || 0));
                  }}
                  className="absolute -inset-y-2 left-0 w-full opacity-0 cursor-pointer"
                  aria-label="seek"
                />
              </div>
              <div className="flex justify-between items-center mt-1 text-[9px] text-[var(--theme-text-muted)] tabular-nums font-mono">
                <span>{fmtTime(current)}</span>
                <div className="flex items-center gap-1.5">
                  <button
                    data-testid="audio-mute-toggle"
                    onClick={() => {
                      if (muted) enableAudio();
                      else { setEnabled(false); localStorage.setItem(ENABLED_KEY, "0"); }
                    }}
                    className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] transition"
                    aria-label={muted ? "unmute" : "mute"}
                  >
                    {muted ? <VolumeX size={10} /> : <Volume2 size={10} />}
                  </button>
                  <input
                    data-testid="audio-volume"
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setVolume(v);
                      // Sliding to 0 = "I want silence" → persist that across reloads.
                      // Sliding up from 0 = re-enable audio.
                      if (v === 0 && enabled) {
                        setEnabled(false);
                      } else if (v > 0 && !enabled) {
                        setEnabled(true);
                      }
                    }}
                    className="w-16 h-[3px] accent-[var(--theme-accent-primary)] cursor-pointer"
                    aria-label="volume"
                  />
                </div>
                <span>{fmtTime(duration)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
